import { useTranslation } from "react-i18next";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { TProjectOverview, TOverviewCriterionStatus } from "@/zapi-frontend/stores/project";
import { refsAt } from "@/zapi-frontend/shared/lib/figureRefs";
import { FigureText } from "../../../MemoIsland/FigureRefs/FigureTrigger";
import { HomeWidget } from "./HomeWidget";
import { STATUS_ORDER } from "./overviewStyles";

const criterionTone: Record<TOverviewCriterionStatus, { dot: string; text: string }> = {
  pass: { dot: "bg-success", text: "text-success" },
  conditional: { dot: "bg-primary-500", text: "text-primary-text" },
  fail: { dot: "bg-destructive", text: "text-destructive" },
  unvalidated: { dot: "bg-greyscale-400", text: "text-muted-foreground" },
  not_defined: { dot: "bg-greyscale-300", text: "text-muted-foreground" }
};

const TARGET_AT = "\u0000";

export function SuccessCriteriaBlock({ overview }: { overview: TProjectOverview }) {
  const { t } = useTranslation(["projectHome", "common"]);
  const { items } = overview.successCriteria;
  if (!items.length) return null;
  const passCount = items.filter((item) => item.status === "pass").length;
  const [targetBefore, targetAfter = ""] = t("projectHome:overview.target", { target: TARGET_AT }).split(TARGET_AT);
  const sortedItems = [...items].sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status));
  const pathOf = (item: (typeof items)[number]) => `successCriteria.items[${items.indexOf(item)}]`;
  const counts = STATUS_ORDER.map((status) => ({
    status,
    count: items.filter((item) => item.status === status).length
  })).filter(({ count }) => count > 0);

  return (
    <HomeWidget title={t("projectHome:overview.successCriteria")} bodyClassName="gap-0 p-0">
      <div className="flex flex-col gap-2.5 border-b px-5 py-4">
        <div className="flex h-1.5 gap-1" role="img" aria-label={`${passCount} / ${items.length}`}>
          {sortedItems.map((item, index) => (
            <span key={index} className={cn("flex-1 rounded-full", criterionTone[item.status].dot)} />
          ))}
        </div>
        <div className="typo-caption flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
          {counts.map(({ status, count }) => (
            <span key={status} className="flex items-center gap-1.5">
              <span className={cn("size-1.5 rounded-full", criterionTone[status].dot)} aria-hidden />
              {t("projectHome:overview.legendCount", {
                value: count,
                status: t(`common:status.${status}`).toLowerCase()
              })}
            </span>
          ))}
        </div>
      </div>
      <ul className="@container/criteria divide-y">
        {sortedItems.map((item, index) => {
          const tone = criterionTone[item.status];
          return (
            <li
              key={index}
              className="grid gap-x-6 gap-y-1.5 px-5 py-4 @xl/criteria:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]"
            >
              <div className="flex flex-col gap-1">
                <span className={cn("typo-label-sm flex items-center gap-1.5", tone.text)}>
                  <span className={cn("size-1.5 rounded-full", tone.dot)} aria-hidden />
                  {t(`common:status.${item.status}`)}
                </span>
                <span className="typo-label-md text-foreground">
                  <FigureText text={item.name} refs={refsAt(overview, `${pathOf(item)}.name`)} />
                </span>
                {item.target && (
                  <span className="typo-caption text-muted-foreground">
                    {targetBefore}
                    <FigureText text={item.target} refs={refsAt(overview, `${pathOf(item)}.target`)} />
                    {targetAfter}
                  </span>
                )}
              </div>
              {item.evidence && (
                <p className="typo-body-sm text-muted-foreground @xl/criteria:pt-5">
                  <FigureText text={item.evidence} refs={refsAt(overview, `${pathOf(item)}.evidence`)} />
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </HomeWidget>
  );
}
